import { PricingSheet, PricingSheetRow } from '../../../shared/types';
import {
  isBelow70PercentDayPriceRow,
  isBelow70PercentPriceRow,
  isDayWarningPrice,
  isWarningPrice,
} from '../utils/pricingSheetRowFilters';
import calculateMeansByGroup from '../utils/calculateMeansByGroup';
import _ from 'lodash';
import { countDaysInDateRange } from '../../../shared/countDaysInDateRange';
import {
  approvalValidatorBase,
  CorporateTitle,
} from './approvalValidatorCommon';

export enum AcceptanceRequirements {
  TotalPriceCoefficient,
  PriceCoefficient,
  PercentageDiscount,
  CriticalEquipment,
  NationWide,
  PaymentTermDays,
  ValidityTime,
}

type RequirementValues = {
  minValue: number;
  maxValue: number;
  label: string;
};

type ValidationValue =
  | number
  | { dayValue?: number; monthValue?: number }
  | undefined;

type AcceptanceMatrixRow = {
  reason: AcceptanceRequirements;
  requirements: Record<CorporateTitle, RequirementValues | undefined>;
  label: string;
  getValueForValidation: (
    pricingSheet: PricingSheet,
    pricingSheetRows: PricingSheetRow[],
  ) => ValidationValue;
  validator: (
    value: ValidationValue,
    requirements?: RequirementValues,
  ) => boolean;
  formatValue: (value: ValidationValue) => string;
};

export const getCorporateTitles = () =>
  Object.values(CorporateTitle).filter(
    (value) => typeof value === 'number',
  );

const commonValidator = (
  value?: ValidationValue,
  requirements?: RequirementValues,
) =>
  typeof value === 'number' &&
  value !== undefined &&
  requirements !== undefined &&
  value < requirements.maxValue &&
  value >= requirements.minValue;

const commonValidatorPricingBasis = (
  value?: ValidationValue,
  requirements?: RequirementValues,
) => {
  if (!value || !requirements) {
    return false;
  }
  return (
    (typeof value === 'object' &&
      value.dayValue !== undefined &&
      value.dayValue < requirements.maxValue &&
      value.dayValue >= requirements.minValue) ||
    (typeof value === 'object' &&
      value.monthValue !== undefined &&
      value.monthValue < requirements.maxValue &&
      value.monthValue >= requirements.minValue)
  );
};

const acceptanceMatrixRows: AcceptanceMatrixRow[] = [
  {
    reason: AcceptanceRequirements.TotalPriceCoefficient,
    label: 'Hinnaston painotettu kokonaiskerroin alittaa (pv/kk)',
    validator: commonValidatorPricingBasis,
    getValueForValidation: (pricingSheet, pricingSheetRows) => {
      const {
        meanFilledCoefficientOfAllDayRowsWeight,
        meanFilledCoefficientOfAllMonthRowsWeight,
      } = calculateMeansByGroup({
        rows: pricingSheetRows,
      });
      return {
        dayValue: meanFilledCoefficientOfAllDayRowsWeight,
        monthValue:
          pricingSheet.pricingBasis !== 'DAY'
            ? meanFilledCoefficientOfAllMonthRowsWeight
            : undefined,
      };
    },
    formatValue: (value) => {
      if (!value || typeof value === 'number') {
        return 'pv: 0 / kk: 0';
      }

      if (!value.dayValue && !value.monthValue) {
        return '-';
      }

      let displayValue = `${value?.dayValue?.toFixed(2) ?? '0'}`;
      if (value?.monthValue) {
        displayValue =
          displayValue + ` / ${value?.monthValue?.toFixed(2) ?? '0'}`;
      }
      return displayValue;
    },
    requirements: {
      [CorporateTitle.AccountManager]: undefined,
      [CorporateTitle.SalesManager]: {
        minValue: 2.0,
        maxValue: 2.5,
        label: 'Alle 2,5',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 1.5,
        maxValue: 2.0,
        label: 'Alle 2,0',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 1.0,
        maxValue: 1.5,
        label: 'Alle 1,5',
      },
      [CorporateTitle.CEO]: {
        minValue: 0,
        maxValue: 1.0,
        label: 'Alle 1,0',
      },
    },
  },
  {
    reason: AcceptanceRequirements.PriceCoefficient,
    label: 'Hintarivien hintakerroin 1 alituksia/hinnasto',
    validator: commonValidator,
    getValueForValidation: (pricingSheet, pricingSheetRows) => {
      const { pricingBasis } = pricingSheet;

      const warningRows =
        pricingBasis === 'DAY'
          ? pricingSheetRows.filter(isDayWarningPrice)
          : pricingSheetRows.filter(isWarningPrice);
      return warningRows.length;
    },
    formatValue: (value) => {
      if (!value || typeof value !== 'number') {
        return '0 kpl';
      }
      return String(Math.round(value)) + ' kpl';
    },
    requirements: {
      [CorporateTitle.AccountManager]: undefined,
      [CorporateTitle.SalesManager]: {
        minValue: 1,
        maxValue: 40,
        label: 'Alle 40 kpl',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 40,
        maxValue: 100,
        label: 'Alle 100 kpl',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 100,
        maxValue: 150,
        label: 'Alle 150',
      },
      [CorporateTitle.CEO]: {
        minValue: 150,
        maxValue: Infinity,
        label: 'Yli 150',
      },
    },
  },
  {
    reason: AcceptanceRequirements.PercentageDiscount,
    label: 'Yli 30% alennus ehdotushinnasta.',
    validator: commonValidator,
    getValueForValidation: (pricingSheet, pricingSheetRows) => {
      const { pricingBasis } = pricingSheet;

      const warningRows =
        pricingBasis === 'DAY'
          ? pricingSheetRows.filter(isBelow70PercentDayPriceRow)
          : pricingSheetRows.filter(isBelow70PercentPriceRow);
      return warningRows.length;
    },
    formatValue: (value) => {
      return String(value) + ' kpl';
    },
    requirements: {
      [CorporateTitle.AccountManager]: {
        minValue: 0,
        maxValue: 0,
        label: 'Alle 30 kpl',
      },
      [CorporateTitle.SalesManager]: {
        minValue: 30,
        maxValue: 100,
        label: 'Alle 100 kpl',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 100,
        maxValue: 150,
        label: 'Alle 150 kpl',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 150,
        maxValue: 200,
        label: 'Alle 200',
      },
      [CorporateTitle.CEO]: {
        minValue: 200,
        maxValue: Infinity,
        label: 'Yli 200',
      },
    },
  },
  {
    reason: AcceptanceRequirements.CriticalEquipment,
    label:
      'Jos kriittisten kaluston osuus yli 50% asiakkaan vuokrauksista ja näiden hintakerroin alittaa',
    validator: commonValidatorPricingBasis,
    getValueForValidation: (pricingSheet, pricingSheetRows) => {
      // Filter critical equipment
      const criticalRows = pricingSheetRows.filter((row) => {
        const criticalItem =
          row.criticalEquipmentItems.length > 0
            ? row.criticalEquipmentItems[0]
            : undefined;
        if (!criticalItem) {
          return false;
        }
        return criticalItem.minPriceDay || criticalItem.minPriceMonth;
      });

      const criticalEquipmentRatio =
        pricingSheetRows.length === 0
          ? 0
          : criticalRows.length / pricingSheetRows.length;

      if (criticalEquipmentRatio <= 0.5) {
        return undefined;
      }
      const {
        meanFilledCoefficientOfAllDayRows,
        meanFilledCoefficientOfAllMonthRows,
      } = calculateMeansByGroup({
        rows: criticalRows,
      });

      return {
        dayValue: meanFilledCoefficientOfAllDayRows ?? undefined,
        monthValue:
          pricingSheet.pricingBasis === 'BOTH'
            ? meanFilledCoefficientOfAllMonthRows ?? undefined
            : undefined,
      };
    },
    formatValue: (value) => {
      if (!value || typeof value === 'number') {
        return 'Osuus alle 50%';
      }
      if (!value) {
        return 'pv: 0 / kk: 0';
      }
      let displayValue = `${value?.dayValue?.toFixed(2) ?? '0'}`;
      if (value?.monthValue) {
        displayValue =
          displayValue + ` / ${value?.monthValue?.toFixed(2) ?? '0'}`;
      }
      return displayValue;
    },
    requirements: {
      [CorporateTitle.AccountManager]: undefined,
      [CorporateTitle.SalesManager]: {
        minValue: 2.5,
        maxValue: 3,
        label: 'Alle 3',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 2,
        maxValue: 2.5,
        label: 'Alle 2,5',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 1.5,
        maxValue: 2,
        label: 'Alle 2',
      },
      [CorporateTitle.CEO]: {
        minValue: 0,
        maxValue: 1.5,
        label: 'Alle 1,5',
      },
    },
  },
  {
    reason: AcceptanceRequirements.NationWide,
    label:
      'Hinnaston kokonaiskerroin alle 2. Toimii valtakunnallisesti',
    validator: commonValidatorPricingBasis,
    getValueForValidation: (pricingSheet, pricingSheetRows) => {
      if (!pricingSheet.nationWide) {
        return undefined;
      }

      const {
        meanFilledCoefficientOfAllDayRows,
        meanFilledCoefficientOfAllMonthRows,
      } = calculateMeansByGroup({
        rows: pricingSheetRows,
      });
      return {
        dayValue: meanFilledCoefficientOfAllDayRows ?? undefined,
        monthValue:
          pricingSheet.pricingBasis !== 'DAY'
            ? meanFilledCoefficientOfAllMonthRows ?? undefined
            : undefined,
      };
    },
    formatValue: (value) => {
      if (!value || typeof value === 'number') {
        return 'Ei toimi valtakunnallisesti';
      }
      if (!value) {
        return 'pv: 0 / kk: 0';
      }
      let displayValue = `${value?.dayValue?.toFixed(2) ?? '0'}`;
      if (value?.monthValue) {
        displayValue =
          displayValue + ` / ${value?.monthValue?.toFixed(2) ?? '0'}`;
      }
      return displayValue;
    },
    requirements: {
      [CorporateTitle.AccountManager]: undefined,
      [CorporateTitle.SalesManager]: undefined,
      [CorporateTitle.RegionalManager]: undefined,
      [CorporateTitle.SalesDirector]: {
        minValue: 0,
        maxValue: 2,
        label: 'Alle 2',
      },
      [CorporateTitle.CEO]: undefined,
    },
  },
  {
    reason: AcceptanceRequirements.PaymentTermDays,
    label: 'Maksuaika',
    validator: commonValidator,
    getValueForValidation: (pricingSheet) => {
      return pricingSheet.paymentTermDays;
    },
    formatValue: (value) => {
      return String(value) + ' pv';
    },
    requirements: {
      [CorporateTitle.AccountManager]: {
        minValue: 0,
        maxValue: 0,
        label: '21 pv',
      },
      [CorporateTitle.SalesManager]: {
        minValue: 30,
        maxValue: 45,
        label: '30 pv',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 0, // The range is "0-0" on purpose. Sales and Regional managers gave same acceptance level and we want the minimum approval level to be Salesmanager
        maxValue: 0,
        label: '30 pv',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 45,
        maxValue: 60,
        label: '45 pv',
      },
      [CorporateTitle.CEO]: {
        minValue: 60,
        maxValue: Infinity,
        label: '60 pv',
      },
    },
  },
  {
    reason: AcceptanceRequirements.ValidityTime,
    label: 'Hinnaston voimassaolo',
    validator: commonValidator,
    getValueForValidation: (pricingSheet) => {
      const { priceSheetStartDate, priceSheetEndDate } =
        pricingSheet as any;
      if (!priceSheetStartDate || !priceSheetEndDate) {
        return 0;
      }
      return (
        countDaysInDateRange(priceSheetStartDate, priceSheetEndDate) /
        365
      );
    },
    formatValue: (value) => {
      if (!value || typeof value !== 'number') {
        return '0';
      }
      return value.toFixed(2) + 'v';
    },
    requirements: {
      [CorporateTitle.AccountManager]: {
        minValue: 0,
        maxValue: 0,
        label: 'Alle 1,5v',
      },
      [CorporateTitle.SalesManager]: {
        minValue: 1.5,
        maxValue: 3.0,
        label: '1,5-3v',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 3,
        maxValue: Infinity,
        label: 'Yli 3v',
      },
      [CorporateTitle.SalesDirector]: undefined,
      [CorporateTitle.CEO]: undefined,
    },
  },
];

export const approvalValidatorPricing = (
  pricingSheet: PricingSheet,
  pricingSheetRows: PricingSheetRow[],
) => {
  return approvalValidatorBase(
    pricingSheet,
    pricingSheetRows,
    acceptanceMatrixRows,
  );
};
